import { type FC, type MouseEvent, useCallback } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import { Alert, Group, Skeleton, Stack, Text } from "@mantine/core";
import { Spacing } from "@snapchat/snap-design-system";

import { AbsoluteRoute } from "../../../core/routes/absoluteRoutes";
import { formatMilliUsd } from "../../../lens-creator-payout/utils/formatMilliUsd";
import { formatWithUSFormat } from "../../../lens-stats/formatters/formatNumbers";
import { StatsCard } from "../../../shared/components/StatsCard";
import { VirtualTable } from "../../../shared/components/virtual-table";
import { type TopPerformerLensRow } from "../../models";
import { lensSummary } from "../LensSummaryGrid.module.css";

import { COLUMN_NAME, topPerformerLensColumns } from "./TopPerformerLensColumns";
import { emptyMessage, heading } from "./TopPerformerLensTable.module.css";
import {
    LensesError,
    LensesSubtitle,
    LensesTitle,
    NoLenses,
    TileAwarded,
    TileAwardedHint,
    TileEnrolled,
    TileEnrolledHint,
    TileTotalEarned,
    TileTotalEarnedHint,
    UpdatedDaily,
} from "./messages.generated";

const FLEX_COLUMNS = [COLUMN_NAME];
const SKELETON_ROW_COUNT = 4;
const SKELETON_ROW_HEIGHT = 36;

interface TopPerformerLensTableProps {
    lenses: TopPerformerLensRow[];
    enrolledCount: number;
    awardedCount: number;
    totalEarnedMilliUsd: number;
    loading: boolean;
    hasError: boolean;
}

export const TopPerformerLensTable: FC<TopPerformerLensTableProps> = ({
    lenses,
    enrolledCount,
    awardedCount,
    totalEarnedMilliUsd,
    loading,
    hasError,
}) => {
    const navigate = useNavigate();
    const handleRowClick = useCallback(
        (row: TopPerformerLensRow) => (event?: MouseEvent) => {
            if (event?.target instanceof Element && event.target.closest("a")) return;
            navigate(generatePath(AbsoluteRoute.LENS, { lensId: row.lens.id }));
        },
        [navigate],
    );

    const body = hasError ? (
        <Alert color="red" variant="light">
            <LensesError />
        </Alert>
    ) : loading ? (
        <Stack gap="md" data-testid="top-performer-lenses-loading">
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
                <Skeleton key={index} h={SKELETON_ROW_HEIGHT} w="100%" />
            ))}
        </Stack>
    ) : (
        <>
            <div className={lensSummary}>
                <StatsCard
                    size="normal"
                    label={<TileEnrolled />}
                    value={formatWithUSFormat(enrolledCount)}
                    diffValue={null}
                    diffText={null}
                    tooltip={<TileEnrolledHint />}
                />
                <StatsCard
                    size="normal"
                    label={<TileAwarded />}
                    value={formatWithUSFormat(awardedCount)}
                    diffValue={null}
                    diffText={null}
                    tooltip={<TileAwardedHint />}
                />
                <StatsCard
                    size="normal"
                    label={<TileTotalEarned />}
                    value={formatMilliUsd(totalEarnedMilliUsd)}
                    diffValue={null}
                    diffText={null}
                    tooltip={<TileTotalEarnedHint />}
                />
            </div>
            <VirtualTable<TopPerformerLensRow>
                data={lenses}
                columns={topPerformerLensColumns}
                flexColumns={FLEX_COLUMNS}
                maxHeight={Spacing["3XL"] * 10}
                onRowClick={handleRowClick}
                emptyMessage={
                    <Text className={`paragraph-p200 ${emptyMessage}`} c="dimmed">
                        <NoLenses />
                    </Text>
                }
            />
        </>
    );

    return (
        <Stack gap="md">
            <div>
                <Group justify="space-between" align="baseline">
                    <h2 className={`heading-h400 ${heading}`}>
                        <LensesTitle />
                    </h2>
                    <Text className="paragraph-p300">
                        <UpdatedDaily />
                    </Text>
                </Group>
                <Text className="paragraph-p300">
                    <LensesSubtitle />
                </Text>
            </div>
            {body}
        </Stack>
    );
};
